'use client';

import './feature-card.scss';

import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.ts';

import { Image } from '../../atoms/image/image.tsx';
import {
  FeatureBase,
  FeatureBaseProps,
} from '../feature-base/feature-base.tsx';
import { Lightbox, LightboxTrigger } from '../lightbox/lightbox.tsx';

export const FeatureCardProps = FeatureBaseProps.extend({
  icon: IconNamesSafe.optional(),
  imagePlacement: z.enum(['left', 'right']).optional(),
  variant: z.enum(['standard', 'horizontal', 'header']).optional(),
  hasNarrowImage: z.boolean().optional(),
  hasPlaceholder: z.boolean().optional(),
  inline: z.boolean().optional(),
});
export type FeatureCardProps = z.infer<typeof FeatureCardProps>;

export const FeatureCard: React.FC<FeatureCardProps> = ({
  seoLevel = 2,
  media,
  mediaLogo,
  imagePlacement,
  variant = 'standard',
  hasNarrowImage = false,
  inline = false,
  hasPlaceholder = false,
  ...props
}) => {
  const { t } = useTranslation(['skippy']);

  const hasRatio = media?.image?.ratio && media.image.ratio !== 'default';
  const defaultRatio = inline ? '4-3' : '16-9';

  const imageRatio = hasRatio ? media?.image?.ratio : defaultRatio;
  const imageRatioMd = hasRatio ? media?.image?.ratio : '4-3';

  const showImage = media?.image || hasPlaceholder;
  const lightboxItems = [];
  const videoIndex = 0;
  if (media?.video) {
    lightboxItems.push({ media });
  }

  return (
    <div
      className={cn(`FeatureCard FeatureCard--${variant}`, {
        'FeatureCard--image-right': imagePlacement === 'right',
        'FeatureCard--image-left': imagePlacement !== 'right',
        'FeatureCard--inline': inline,
        'has-image': media?.image,
        'has-narrow-image': (media?.image || hasPlaceholder) && hasNarrowImage,
      })}
    >
      {showImage && lightboxItems.length > 0 && (
        <div className="FeatureCard__media w-full">
          <Lightbox items={[{ media }]} startIndex={videoIndex}>
            <LightboxTrigger
              className="Lightbox__trigger w-full h-full"
              ariaLabel={t('skippy:gallery.open')}
            >
              <Image
                image={media?.image}
                className={cn('FeatureCard__image')}
                aspectRatio={imageRatio}
                aspectRatioMd={imageRatioMd}
                rounded={inline ? 'lg' : undefined}
                hasPlaceholder
                isVideoThumbnail
                videoDuration={media?.video?.duration}
              />
            </LightboxTrigger>
          </Lightbox>
        </div>
      )}
      {showImage && lightboxItems.length === 0 && (
        <div className="FeatureCard__media w-full">
          <Image
            image={media?.image}
            className={cn('FeatureCard__image')}
            aspectRatio={imageRatio}
            aspectRatioMd={imageRatioMd}
            rounded={inline ? 'lg' : undefined}
            hasPlaceholder
          />
        </div>
      )}
      <div className="FeatureCard__flex w-full">
        <div
          className={cn('FeatureCard__content w-full max-w-md', {
            'FeatureCard__content-right': imagePlacement === 'left',
            'FeatureCard__content-left': imagePlacement !== 'left',
          })}
        >
          <FeatureBase
            seoLevel={seoLevel}
            {...props}
            {...(props.primaryCTA &&
            !props.tertiaryCTA &&
            props.secondaryCTAVariant !== 'tertiary'
              ? {
                  secondaryCTAIconName: 'arrow-right',
                  secondaryCTAIconPlacement: 'after',
                }
              : {})}
            media={mediaLogo}
            programmaticButtons={
              media?.video && media.video.hasProgrammaticButton ? (
                <Lightbox
                  key="jumbotron-profound-lightbox"
                  items={[
                    {
                      media,
                    },
                  ]}
                  startIndex={0}
                >
                  <LightboxTrigger className="flex">
                    <ButtonStyle
                      iconBefore={<ArkIcon iconName="play-circle" />}
                      variant={props.secondaryCTA ? 'tertiary' : 'secondary'}
                    >
                      <span>{t('skippy:video.watch')}</span>
                    </ButtonStyle>
                  </LightboxTrigger>
                </Lightbox>
              ) : undefined
            }
            inline
          />
        </div>
      </div>
    </div>
  );
};
